import { Flex, ListItem, OrderedList, Text } from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";

import { PublicationRead } from "@nens/rana/api-v1";
import { RanaErrors, RanaLoading, RanaModal } from "@nens/ranalib";

import { cancelButtonText } from "@/translations";

import { useDeletePublication } from "../../../api/delete";

type PublicationDeleteModalProps = {
  publications: PublicationRead[];
  onClose: () => void;
};

export function PublicationDeleteModal({ publications, onClose }: PublicationDeleteModalProps) {
  const intl = useIntl();
  const { mutateAsync, isMutating, errors } = useDeletePublication();

  const isMultiple = publications.length > 1;

  const title = intl.formatMessage(
    isMultiple
      ? {
          id: "publication.delete_publications",
          defaultMessage: "Delete publications",
        }
      : {
          id: "publication.delete_publication",
          defaultMessage: "Delete publication",
        }
  );

  const deletePublications = async () => {
    for (const publication of publications) {
      await mutateAsync({ publicationId: publication.id });
    }

    onClose();
  };

  return (
    <RanaModal
      isOpen
      onClose={onClose}
      topBar={title}
      maxW="xl"
      h="fit"
      buttons={[
        {
          children: cancelButtonText,
          variant: "tertiary",
          isDisabled: isMutating,
          onClick: onClose,
        },
        {
          children: title,
          variant: "danger",
          isDisabled: isMutating,
          onClick: deletePublications,
        },
      ]}
    >
      <Flex flexDir="column" gap={2}>
        <Text>
          {isMultiple ? (
            <FormattedMessage
              id="publication.delete_are_you_sure_selected"
              defaultMessage={"Are you sure you want to delete these publications?"}
            />
          ) : (
            <FormattedMessage
              id="publication.delete_are_you_sure_selected"
              defaultMessage={"Are you sure you want to delete this publication?"}
            />
          )}
        </Text>

        {isMultiple && (
          <OrderedList>
            {publications.map(({ id, name }) => (
              <ListItem key={id}>
                <Flex alignItems="center">{name}</Flex>
              </ListItem>
            ))}
          </OrderedList>
        )}

        {isMutating && <RanaLoading size="md" isCentered />}
        <RanaErrors errors={errors} />
      </Flex>
    </RanaModal>
  );
}
